import React from "react";
import { useHistory } from "react-router-dom";
import OTLayout from "./OTLayout";
import { OT_BASE, C } from "./constants";
import { WORKFLOWS } from "./workflows";
import { DASHBOARD_STATS, UPCOMING_CASES } from "./mockData";
import {
  OtPage,
  SectionCard,
  KpiTile,
  OtButton,
  OtLinkButton,
  OtListButton,
  OtCardButton,
  OtDataTable,
  CategoryBadge,
  caseStatusTone,
  StatusPill,
  Box,
  Grid,
  Typography,
  Stack,
  eyebrowSx,
  titleSx,
  subtitleSx,
} from "./ui";
import SvgIcon from "../ipd-billing/SvgIcon";

function todayLabel() {
  return new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

export default function OTDashboard() {
  const history = useHistory();

  return (
    <OTLayout activeNav="dashboard">
      <OtPage>
        <SectionCard sx={{ p: 3 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "flex-end" }} spacing={2}>
            <Box>
              <Typography sx={eyebrowSx}>Today · {todayLabel()}</Typography>
              <Typography component="h1" sx={titleSx}>Operation Theatre command center</Typography>
              <Typography sx={{ ...subtitleSx, maxWidth: 640 }}>
                Track scheduling, pre-op clearance, intra-operative status and post-op recovery across every theatre.
              </Typography>
            </Box>
            <OtButton
              onClick={() => history.push(`${OT_BASE}/schedule`)}
              startIcon={<SvgIcon name="clock" size={14} color="#fff" />}
            >
              Open OT schedule
            </OtButton>
          </Stack>

          <Grid container spacing={1.5} sx={{ mt: 2.5 }}>
            {DASHBOARD_STATS.map((s) => (
              <Grid item xs={6} md={3} key={s.label}>
                <KpiTile icon={s.icon} label={s.label} value={s.value} tone={s.tone} />
              </Grid>
            ))}
          </Grid>
        </SectionCard>

        <Grid container spacing={3} sx={{ mt: 3 }}>
          <Grid item xs={12} lg={8}>
            <SectionCard sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700 }}>Today&apos;s OT list</Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: C.textSub }}>
                    {UPCOMING_CASES.length} of 27 cases · live status
                  </Typography>
                </Box>
                <OtLinkButton onClick={() => history.push(`${OT_BASE}/schedule`)}>View all</OtLinkButton>
              </Stack>
              <Box sx={{ mt: 2 }}>
                <OtDataTable
                  columns={[
                    { key: "time", label: "Time", cellSx: () => ({ fontWeight: 600 }) },
                    { key: "ot", label: "OT", cellSx: () => ({ color: C.textSub }) },
                    { key: "patient", label: "Patient" },
                    { key: "procedure", label: "Procedure" },
                    { key: "surgeon", label: "Surgeon", cellSx: () => ({ color: C.textSub }) },
                    {
                      key: "status",
                      label: "Status",
                      render: (row) => <StatusPill label={row.status} tone={caseStatusTone(row.status)} />,
                    },
                  ]}
                  rows={UPCOMING_CASES}
                  getRowKey={(c) => `${c.time}-${c.ot}`}
                />
              </Box>
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={4}>
            <SectionCard sx={{ p: 3 }}>
              <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700 }}>Quick workflows</Typography>
              <Typography sx={{ mt: 0.5, fontSize: 13, color: C.textSub }}>Jump into a standard pathway</Typography>
              <Stack spacing={1} sx={{ mt: 2 }}>
                {WORKFLOWS.slice(0, 6).map((w) => (
                  <OtListButton
                    key={w.slug}
                    onClick={() => history.push(`${OT_BASE}/workflows/${w.slug}`)}
                    endIcon={
                      <Box sx={{ display: "inline-flex", transform: "rotate(180deg)" }}>
                        <SvgIcon name="back" size={14} color={C.textSub} />
                      </Box>
                    }
                  >
                    <Box sx={{ minWidth: 0, textAlign: "left" }}>
                      <Typography sx={{ fontSize: 11, color: C.textSub }}>{w.code}</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>{w.title}</Typography>
                    </Box>
                  </OtListButton>
                ))}
                <OtLinkButton onClick={() => history.push(`${OT_BASE}/workflows`)} sx={{ mt: 0.5, alignSelf: "center" }}>
                  See all 12 workflows
                </OtLinkButton>
              </Stack>
            </SectionCard>
          </Grid>
        </Grid>

        <SectionCard sx={{ mt: 3, p: 3 }}>
          <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700 }}>Standard OT workflows</Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: C.textSub }}>
            All 12 pathways governing the operation theatre.
          </Typography>
          <Grid container spacing={1.5} sx={{ mt: 2 }}>
            {WORKFLOWS.map((w) => (
              <Grid item xs={12} sm={6} md={4} key={w.slug}>
                <OtCardButton onClick={() => history.push(`${OT_BASE}/workflows/${w.slug}`)}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%" }}>
                    <Typography sx={{ fontSize: 11, fontFamily: "monospace", color: C.textSub }}>{w.code}</Typography>
                    <CategoryBadge category={w.category} />
                  </Stack>
                  <Typography sx={{ mt: 1.25, fontSize: 14, fontWeight: 700, color: C.text, textAlign: "left" }}>{w.title}</Typography>
                  <Typography sx={{ mt: 0.75, fontSize: 12, color: C.textSub, lineHeight: 1.4, textAlign: "left" }}>{w.summary}</Typography>
                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.5, width: "100%", fontSize: 11, color: C.textSub }}>
                    <Typography sx={{ fontSize: 11, color: C.textSub }}>{w.steps.length} steps</Typography>
                    <Typography sx={{ fontSize: 11, color: C.primary, fontWeight: 600 }}>Open →</Typography>
                  </Stack>
                </OtCardButton>
              </Grid>
            ))}
          </Grid>
        </SectionCard>
      </OtPage>
    </OTLayout>
  );
}
